import {
  OverlayType,
  DEFAULT_OVERLAY_STYLE,
  type AnyOverlay,
  type OverlayStyle,
  type PointOverlay,
  type LineOverlay,
  type PolylineOverlay,
  type CircleOverlay,
  type ArcOverlay,
  type RectangleOverlay,
  type TextOverlay,
  type EllipseOverlay,
} from "./types";

/**
 * OverlayRenderer - Renders overlay graphics to a 2D canvas.
 *
 * Uses a separate canvas element positioned over the main WebGL canvas
 * for efficient preview rendering without re-rendering the 3D scene.
 */
export class OverlayRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Failed to get 2D context for overlay canvas");
    }
    this.ctx = ctx;
  }

  /**
   * Clear the entire overlay canvas.
   */
  clear(): void {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  /**
   * Resize the overlay canvas to match dimensions.
   */
  resize(width: number, height: number): void {
    this.canvas.width = width;
    this.canvas.height = height;
  }

  /**
   * Render a single overlay geometry.
   */
  render(overlay: AnyOverlay): void {
    this.applyStyle(overlay.style);

    switch (overlay.type) {
      case OverlayType.Point:
        this.renderPoint(overlay);
        break;
      case OverlayType.Line:
        this.renderLine(overlay);
        break;
      case OverlayType.Polyline:
        this.renderPolyline(overlay);
        break;
      case OverlayType.Circle:
        this.renderCircle(overlay);
        break;
      case OverlayType.Arc:
        this.renderArc(overlay);
        break;
      case OverlayType.Rectangle:
        this.renderRectangle(overlay);
        break;
      case OverlayType.Text:
        this.renderText(overlay);
        break;
      case OverlayType.Ellipse:
        this.renderEllipse(overlay);
        break;
    }
  }

  /**
   * Render multiple overlays.
   */
  renderAll(overlays: AnyOverlay[]): void {
    for (const overlay of overlays) {
      this.render(overlay);
    }
  }

  // ============== Private Render Methods ==============

  private applyStyle(style: OverlayStyle): void {
    const merged = { ...DEFAULT_OVERLAY_STYLE, ...style };

    this.ctx.strokeStyle = merged.strokeColor ?? "#0088ff";
    this.ctx.lineWidth = merged.strokeWidth ?? 2;
    this.ctx.fillStyle = merged.fillColor ?? "transparent";
    this.ctx.setLineDash(merged.lineDash ?? []);
    this.ctx.globalAlpha = merged.opacity ?? 1;
    this.ctx.lineCap = "round";
    this.ctx.lineJoin = "round";
  }

  private renderPoint(overlay: PointOverlay): void {
    const radius = overlay.radius ?? 4;

    this.ctx.beginPath();
    this.ctx.arc(overlay.x, overlay.y, radius, 0, Math.PI * 2);

    if (overlay.style.fillColor) {
      this.ctx.fill();
    }
    this.ctx.stroke();
  }

  private renderLine(overlay: LineOverlay): void {
    this.ctx.beginPath();
    this.ctx.moveTo(overlay.x1, overlay.y1);
    this.ctx.lineTo(overlay.x2, overlay.y2);
    this.ctx.stroke();
  }

  private renderPolyline(overlay: PolylineOverlay): void {
    if (!overlay.points.length) return;

    // A single-point polyline is a pen tap (e.g. the dot of an "i"):
    // stroke() renders nothing for a lone point, so draw a small dot in
    // the stroke color instead.
    if (overlay.points.length === 1) {
      const p = overlay.points[0];
      const previousFill = this.ctx.fillStyle;
      this.ctx.fillStyle = this.ctx.strokeStyle;
      this.ctx.beginPath();
      this.ctx.arc(p.x, p.y, Math.max(this.ctx.lineWidth, 1.5), 0, Math.PI * 2);
      this.ctx.fill();
      this.ctx.fillStyle = previousFill;
      return;
    }

    this.ctx.beginPath();
    this.ctx.moveTo(overlay.points[0].x, overlay.points[0].y);

    for (let i = 1; i < overlay.points.length; i++) {
      this.ctx.lineTo(overlay.points[i].x, overlay.points[i].y);
    }

    if (overlay.closed) {
      this.ctx.closePath();
    }

    if (overlay.style.fillColor) {
      this.ctx.fill();
    }
    this.ctx.stroke();
  }

  private renderCircle(overlay: CircleOverlay): void {
    this.ctx.beginPath();
    this.ctx.arc(overlay.cx, overlay.cy, overlay.radius, 0, Math.PI * 2);

    if (overlay.style.fillColor) {
      this.ctx.fill();
    }
    this.ctx.stroke();
  }

  private renderArc(overlay: ArcOverlay): void {
    this.ctx.beginPath();
    this.ctx.arc(
      overlay.cx,
      overlay.cy,
      overlay.radius,
      overlay.startAngle,
      overlay.endAngle,
      overlay.counterClockwise ?? false,
    );

    if (overlay.style.fillColor) {
      this.ctx.fill();
    }
    this.ctx.stroke();
  }

  private renderRectangle(overlay: RectangleOverlay): void {
    if (overlay.style.fillColor) {
      this.ctx.fillRect(overlay.x, overlay.y, overlay.width, overlay.height);
    }
    this.ctx.strokeRect(overlay.x, overlay.y, overlay.width, overlay.height);
  }

  private renderText(overlay: TextOverlay): void {
    const fontSize = overlay.fontSize ?? 14;
    const fontFamily = overlay.fontFamily ?? "Arial, sans-serif";
    const textAlign = overlay.textAlign ?? "left";
    const textBaseline = overlay.textBaseline ?? "top";

    this.ctx.font = `${fontSize}px ${fontFamily}`;
    this.ctx.textAlign = textAlign;
    this.ctx.textBaseline = textBaseline;

    // Draw background if specified
    if (overlay.backgroundColor) {
      const padding = overlay.padding ?? 4;
      const metrics = this.ctx.measureText(overlay.text);
      const textWidth = metrics.width;
      const textHeight = fontSize;

      // Calculate background rect position based on alignment
      let bgX = overlay.x - padding;
      if (textAlign === "center") bgX = overlay.x - textWidth / 2 - padding;
      else if (textAlign === "right") bgX = overlay.x - textWidth - padding;

      let bgY = overlay.y - padding;
      if (textBaseline === "middle") bgY = overlay.y - textHeight / 2 - padding;
      else if (textBaseline === "bottom" || textBaseline === "alphabetic")
        bgY = overlay.y - textHeight - padding;

      const bgW = textWidth + padding * 2;
      const bgH = textHeight + padding * 2;
      const radius = 3;

      this.ctx.save();
      this.ctx.globalAlpha = overlay.style.opacity ?? 1;
      this.ctx.fillStyle = overlay.backgroundColor;
      this.ctx.beginPath();
      this.ctx.roundRect(bgX, bgY, bgW, bgH, radius);
      this.ctx.fill();
      this.ctx.restore();
    }

    // Render text (reapply style since save/restore may have changed it)
    this.applyStyle(overlay.style);
    this.ctx.font = `${fontSize}px ${fontFamily}`;
    this.ctx.textAlign = textAlign;
    this.ctx.textBaseline = textBaseline;

    if (overlay.style.fillColor) {
      this.ctx.fillText(overlay.text, overlay.x, overlay.y);
    }
    if (overlay.style.strokeColor) {
      this.ctx.strokeText(overlay.text, overlay.x, overlay.y);
    }

    // Reset alignment to defaults so other overlays aren't affected
    this.ctx.textAlign = "left";
    this.ctx.textBaseline = "top";
  }

  private renderEllipse(overlay: EllipseOverlay): void {
    this.ctx.beginPath();
    this.ctx.ellipse(
      overlay.cx,
      overlay.cy,
      overlay.radiusx,
      overlay.radiusy,
      overlay.rotation || 0,
      0,
      2 * Math.PI,
    );

    if (overlay.style.fillColor) {
      this.ctx.fill();
    }

    this.ctx.stroke();
  }
}
